import { Link2, Package, Plus, ShoppingCart, Trash2, User } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '../../../components/Button';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { ticketErrorRO } from '../ticketCopy';
import { useDeleteReferenceMutation, useListReferencesQuery } from '../tickets.api';
import {
    referenceHref,
    TICKET_REFERENCE_LABELS,
    type TicketReference,
    type TicketReferenceType,
} from '../tickets.schema';

import { AddReferenceDialog } from './AddReferenceDialog';

const ICONS: Record<string, typeof Link2> = {
    order: ShoppingCart,
    user: User,
    product: Package,
};

export function ReferencesPanel({ id }: { id: number }) {
    const { data: refs, isLoading } = useListReferencesQuery(id);
    const [deleteReference] = useDeleteReferenceMutation();
    const [adding, setAdding] = useState(false);
    const [pendingDelete, setPendingDelete] = useState<TicketReference | null>(null);

    const onDelete = async () => {
        if (!pendingDelete) return;
        try {
            await deleteReference({ id, refId: pendingDelete.id }).unwrap();
            toast.success('Legatura a fost stearsa.');
        } catch (e) {
            toast.error(ticketErrorRO(e, 'Eroare la stergere.'));
        } finally {
            setPendingDelete(null);
        }
    };

    const items = refs ?? [];

    return (
        <div>
            <div className="mb-3 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Legaturi</h2>
                <Button variant="secondary" onClick={() => setAdding(true)}>
                    <Plus aria-hidden="true" className="h-4 w-4" />
                    Adauga
                </Button>
            </div>

            {isLoading ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Se incarca...</p>
            ) : items.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">Nicio legatura.</p>
            ) : (
                <ul className="flex flex-col gap-2">
                    {items.map((r) => {
                        const href = referenceHref(r);
                        const Icon = ICONS[r.itemType] ?? Link2;
                        const typeLabel = TICKET_REFERENCE_LABELS[r.itemType as TicketReferenceType] ?? r.itemType;
                        const text = r.label || `${typeLabel} ${r.itemId}`;
                        return (
                            <li
                                key={r.id}
                                className="flex items-center justify-between gap-3 rounded-xl border border-solid border-gray-200 bg-white px-3 py-2.5 shadow-sm dark:border-gray-700 dark:bg-gray-800"
                            >
                                <span className="inline-flex min-w-0 items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
                                    <Icon aria-hidden="true" className="h-4 w-4 shrink-0 text-gray-400" />
                                    {href ? (
                                        <a
                                            href={href}
                                            className="truncate text-blue-600 hover:underline dark:text-blue-400"
                                        >
                                            {text}
                                        </a>
                                    ) : (
                                        <span className="truncate">{text}</span>
                                    )}
                                    <span className="shrink-0 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                                        {typeLabel}
                                    </span>
                                </span>
                                <Button
                                    variant="ghost"
                                    className="h-10 w-10 p-0"
                                    aria-label="Sterge legatura"
                                    onClick={() => setPendingDelete(r)}
                                >
                                    <Trash2 aria-hidden="true" className="h-4 w-4 text-red-600 dark:text-red-400" />
                                </Button>
                            </li>
                        );
                    })}
                </ul>
            )}

            <AddReferenceDialog id={id} open={adding} onOpenChange={setAdding} />
            <ConfirmDialog
                open={pendingDelete !== null}
                onClose={() => setPendingDelete(null)}
                onConfirm={onDelete}
                title="Sterge legatura?"
                description={pendingDelete ? `"${pendingDelete.label || pendingDelete.itemId}" va fi eliminata.` : ''}
                confirmLabel="Sterge"
                variant="danger"
            />
        </div>
    );
}
